import React, { useContext, useMemo, useCallback } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsToHideMultipleSelect.module.scss';

interface IRestaurantsToHideMultipleSelect {
	setIDsOfRestaurantsWhereToHide: (value: string[]) => void;
	IDSelectedCustomer: Nullable<string>;
	fetchedRestaurants: IItemForGetShortInfoWithStatus[];
	IDsOfRestaurantsWhereToHide: string[];
	IDsSelectedRestaurant: string[];
}

const RestaurantsToHideMultipleSelect: React.FC<IRestaurantsToHideMultipleSelect> = ({
	setIDsOfRestaurantsWhereToHide,
	IDSelectedCustomer,
	fetchedRestaurants,
	IDsOfRestaurantsWhereToHide,
	IDsSelectedRestaurant,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const restaurants = useMemo(() => {
		return hasAccess ? fetchedRestaurants : loggedUser.restaurants;
	}, [hasAccess, fetchedRestaurants, loggedUser]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsOfRestaurantsWhereToHide.filter((el) => el !== id);
			setIDsOfRestaurantsWhereToHide(updatedSelectedItems);
		},
		[IDsOfRestaurantsWhereToHide, setIDsOfRestaurantsWhereToHide],
	);

	const restaurantsList = useMemo(() => {
		return IDsOfRestaurantsWhereToHide.map((el) => {
			const label = restaurants?.find((item) => item.id === el)?.name || '';

			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [IDsOfRestaurantsWhereToHide, restaurants, handleRemove]);

	return (
		<div className={style.container} data-test="RestaurantsToHideMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={restaurants || []}
					setIDsOfRestaurantsWhereToHide={setIDsOfRestaurantsWhereToHide}
					IDSelectedCustomer={IDSelectedCustomer}
					IDsOfRestaurantsWhereToHide={IDsOfRestaurantsWhereToHide}
					IDsSelectedRestaurant={IDsSelectedRestaurant}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsToHideMultipleSelect);
